/**
 * Hurka — shared types for client and server.
 *
 * Anything that crosses the wire or is referenced by both halves of the
 * codebase lives here.  This file is the single source of truth for the
 * WebSocket protocol.
 */

// ---------------------------------------------------------------------------
// Wire envelope — every message has a `type` discriminator.
// ---------------------------------------------------------------------------

export type WireMessage =
  | ServerHelloMessage
  | ClientHelloMessage
  | PingMessage
  | PongMessage
  | ClientNameMessage
  | ClientMoveMessage
  | ClientChatMessage
  | ClientAttackMessage
  | ClientPickupMessage
  | WorldStateMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | ChatBroadcastMessage
  | EnemyStateMessage
  | EnemyDiedMessage
  | LootSpawnMessage
  | LootPickupMessage
  | CombatDamageMessage
  | ClientHouseEnterMessage
  | ClientHouseExitMessage
  | HouseEnteredMessage
  | HouseExitedMessage
  | InteriorFurnitureMessage;

// ---------------------------------------------------------------------------
// Connection lifecycle (#6)
// ---------------------------------------------------------------------------

export interface ServerHelloMessage {
  type: 'server.hello';
  playerId: string;
  serverTime: number;
}

export interface ClientHelloMessage {
  type: 'client.hello';
  clientTime: number;
}

export interface PingMessage {
  type: 'ping';
  t: number;
}

export interface PongMessage {
  type: 'pong';
  t: number;
}

// ---------------------------------------------------------------------------
// Player identity + movement (#7, #8)
// ---------------------------------------------------------------------------

export type WorldMode = 'world' | 'interior';

export interface PlayerState {
  playerId: string;
  name: string | null;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /**
   * Where the player currently is.  Players in `interior` mode are only
   * visible to other players sharing the same `interiorId`; players in
   * `world` mode are only visible to other players in `world` mode.
   * Defaults to `world` for back-compat with pre-#16 clients.
   */
  mode?: WorldMode;
  interiorId?: string;
}

export interface ClientNameMessage {
  type: 'client.name';
  name: string;
}

export interface ClientMoveMessage {
  type: 'client.move';
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface WorldStateMessage {
  type: 'world.state';
  players: PlayerState[];
  enemies: EnemyState[];
  loot: LootState[];
  serverTime: number;
  /** Always present from #16 onward — clients ignore it if absent. */
  mode?: WorldMode;
  interiorId?: string;
  /**
   * When in interior mode, the full furniture / interactable list for that
   * interior (for first-time entry) so the client can populate the room
   * before the player moves around.  The server only sends this once per
   * entry; clients are expected to keep their own copy after that.
   */
  furniture?: InteriorFurniture[];
}

export interface PlayerJoinedMessage {
  type: 'player.joined';
  player: PlayerState;
}

export interface PlayerLeftMessage {
  type: 'player.left';
  playerId: string;
}

// ---------------------------------------------------------------------------
// Chat (#9)
// ---------------------------------------------------------------------------

export interface ChatLine {
  from: string;
  text: string;
  ts: number;
}

export interface ClientChatMessage {
  type: 'client.chat';
  text: string;
}

export interface ClientAttackMessage {
  type: 'client.attack';
  enemyId: string;
  t: number;
}

export interface ClientPickupMessage {
  type: 'client.pickup';
  lootId: string;
}

export interface ChatBroadcastMessage {
  type: 'chat.broadcast';
  line: ChatLine;
}

// ---------------------------------------------------------------------------
// Enemies (#10, #11)
// ---------------------------------------------------------------------------

export interface EnemyState {
  enemyId: string;
  kind: 'goblin';
  x: number;
  y: number;
  z: number;
  hp: number;
  maxHp: number;
  state: 'idle' | 'patrol' | 'chase' | 'dead';
}

export interface EnemyStateMessage {
  type: 'enemy.state';
  enemy: EnemyState;
}

export interface EnemyDiedMessage {
  type: 'enemy.died';
  enemyId: string;
  killerId: string;
  position: { x: number; y: number; z: number };
}

// ---------------------------------------------------------------------------
// Combat (#11, #12)
// ---------------------------------------------------------------------------

export interface CombatDamageMessage {
  type: 'combat.damage';
  enemyId: string;
  attackerId: string;
  amount: number;
  remainingHp: number;
}

// ---------------------------------------------------------------------------
// Loot (#12, #13)
// ---------------------------------------------------------------------------

export interface LootState {
  lootId: string;
  itemId: string;
  x: number;
  y: number;
  z: number;
  /** Set when a player has picked it up (server still keeps it for one
   *  tick so the renderer can fade it out). */
  claimedBy?: string;
}

export interface LootSpawnMessage {
  type: 'loot.spawn';
  loot: LootState;
}

export interface LootPickupMessage {
  type: 'loot.pickup';
  lootId: string;
  playerId: string;
}

// ---------------------------------------------------------------------------
// Houses + Interiors (#16 — 1 betretbares Haus)
// ---------------------------------------------------------------------------

/**
 * Door trigger positions the server checks.  Hard-coded here because the
 * client also uses them for the highlight + HUD hint distance test, and
 * keeping them in one place avoids drift between the two halves.
 */
export const HOUSE_DOOR_REACH_M = 2.0;
export const INTERIOR_DOOR_REACH_M = 1.8;

/** Position of the (single) outdoor door for the showcase house. */
export const SHOWCASE_HOUSE = {
  houseId: 'house-west',
  /** World-space center of the door pivot, on the ground. */
  doorX: -12,
  doorY: 0,
  doorZ: -3.5,
  /** Heading the player is given when exiting back outside.  0 = facing
   *  +Z (south, into the courtyard). */
  exitYaw: 0,
} as const;

/** Layout of the interior of the showcase house. */
export const SHOWCASE_INTERIOR = {
  interiorId: 'interior-house-west',
  /** Interior spawn point when entering (right inside the door). */
  spawnX: 0,
  spawnY: 1.7,
  spawnZ: 4.5,
  /** Exit-door position inside the interior (where [E] sends you back out). */
  exitX: 0,
  exitZ: 4.5,
  /** Floor footprint in metres (centred on interior origin). */
  width: 6,
  depth: 9,
  /** Wall + ceiling heights. */
  groundFloorHeight: 3.0,
  upperFloorHeight: 2.8,
} as const;

export interface InteriorFurniture {
  furnitureId: string;
  kind: 'table' | 'chair' | 'bed' | 'shelf' | 'chest' | 'barrel' | 'woodpile' | 'hearth' | 'stair' | 'fire';
  x: number;
  y: number;
  z: number;
  rotationY?: number;
  /** Optional size hint; client picks a sensible default if absent. */
  w?: number;
  h?: number;
  d?: number;
}

export interface ClientHouseEnterMessage {
  type: 'client.house.enter';
  houseId: string;
}

export interface ClientHouseExitMessage {
  type: 'client.house.exit';
}

export interface HouseEnteredMessage {
  type: 'house.entered';
  houseId: string;
  interiorId: string;
  spawn: { x: number; y: number; z: number; yaw: number };
  /** Server-snapshot of the furniture for this interior (one-shot). */
  furniture: InteriorFurniture[];
}

export interface HouseExitedMessage {
  type: 'house.exited';
  /** Where the player ends up when stepping out of the door. */
  spawn: { x: number; y: number; z: number; yaw: number };
}

export interface InteriorFurnitureMessage {
  type: 'interior.furniture';
  interiorId: string;
  furniture: InteriorFurniture[];
}

// ---------------------------------------------------------------------------
// Limits (shared constants the server enforces too)
// ---------------------------------------------------------------------------

export const MAX_NAME_LEN = 16;
export const MAX_CHAT_LEN = 200;
